import React from "react";
import type { ReactNode } from "react";
import classNames from "classnames";
import styles from "./CustomCard.module.css";

export interface CustomCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The title of the card
   */
  title?: ReactNode;
  /**
   * Content of the card
   */
  children?: ReactNode;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Whether to show border
   * @default false
   */
  bordered?: boolean;
  /**
   * Content to render in the top-right corner of the header
   */
  extra?: ReactNode;
  /**
   * Inline style
   */
  style?: React.CSSProperties;
  /**
   * Body style
   */
  bodyStyle?: React.CSSProperties;
}

/**
 * CustomCard Component
 *
 * A replacement for Ant Design Card with custom styling, hover effects, and responsive design.
 */
export const CustomCard: React.FC<CustomCardProps> = ({ title, children, className, bordered = false, extra, style, bodyStyle, ...rest }) => {
  return (
    <div
      className={classNames(
        styles.card,
        {
          [styles.bordered]: bordered,
        },
        className
      )}
      style={style}
      {...rest}
    >
      {(title || extra) && (
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          {extra && <div className={styles.extra}>{extra}</div>}
        </div>
      )}
      <div className={styles.body} style={bodyStyle}>
        {children}
      </div>
    </div>
  );
};

export default CustomCard;
